// client/src/hooks/useCallBoard.js
//
// Store-wide live calls for receptionists and managers, from the backend's
// call_board_* WebSocket messages. `ready` flips once the first snapshot
// arrives, so a backend without the board (older deploy) leaves the old
// Live Calls feed in place instead of showing an empty board.
import { useCallback, useMemo, useState } from 'react';

const byNewest = (a, b) => (b.firstSeen || 0) - (a.firstSeen || 0);

export function applyBoardMessage(state, message) {
  if (message?.type === 'call_board_snapshot') {
    const calls = new Map();
    for (const c of message.calls || []) if (c?.callId) calls.set(c.callId, c);
    return { ready: true, visibility: message.visibility || state.visibility, calls };
  }
  if (message?.type === 'call_board_update' && message.call?.callId) {
    const calls = new Map(state.calls);
    calls.set(message.call.callId, message.call);
    return { ...state, calls };
  }
  if (message?.type === 'call_board_remove' && message.callId) {
    if (!state.calls.has(message.callId)) return state;
    const calls = new Map(state.calls);
    calls.delete(message.callId);
    return { ...state, calls };
  }
  return state;
}

export const EMPTY_BOARD = { ready: false, visibility: null, calls: new Map() };

export default function useCallBoard() {
  const [board, setBoard] = useState(EMPTY_BOARD);
  const handleBoardMessage = useCallback((message) => {
    if (typeof message?.type !== 'string' || !message.type.startsWith('call_board_')) return false;
    setBoard((s) => applyBoardMessage(s, message));
    return true;
  }, []);
  const resetBoard = useCallback(() => setBoard(EMPTY_BOARD), []);
  const boardCalls = useMemo(() => [...board.calls.values()].sort(byNewest), [board.calls]);
  return { boardCalls, boardReady: board.ready, boardVisibility: board.visibility, handleBoardMessage, resetBoard };
}
